import React from 'react';
import { Info, TrendingUp, PercentSquare, Calculator, CircleDollarSign, BarChart, LayoutList, AlertTriangle } from 'lucide-react';

/**
 * Компонент с информацией о рентабельности и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-emerald-500" />
        Всё о рентабельности: показатели, анализ и оптимизация
      </h2>
      
      <div className="prose prose-emerald dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          Что такое рентабельность?
        </h3>
        <p>
          <strong>Рентабельность</strong> — это один из важнейших экономических показателей, характеризующий эффективность деятельности предприятия. Она показывает, насколько прибыльно работает бизнес, соотнося полученный доход с затраченными ресурсами. Рентабельность измеряется в процентах и рассчитывается как отношение прибыли к затраченным ресурсам (выручке, активам, капиталу).
        </p>

        <h3 className="flex items-center gap-2">
          <PercentSquare className="w-5 h-5 text-emerald-500" />
          Ключевые показатели рентабельности
        </h3>
        <ul>
          <li><strong>Валовая маржа (Gross Profit Margin):</strong> Показывает, какая часть выручки остается после вычета прямых затрат на производство товаров или услуг. Рассчитывается как (Выручка - Себестоимость) / Выручка * 100%.</li>
          <li><strong>Операционная маржа (Operating Margin):</strong> Отражает эффективность основной деятельности компании. Рассчитывается как Операционная прибыль / Выручка * 100%.</li>
          <li><strong>Чистая маржа (Net Profit Margin):</strong> Показывает, какая часть выручки остается у компании после уплаты всех затрат и налогов. Рассчитывается как Чистая прибыль / Выручка * 100%.</li>
          <li><strong>Рентабельность инвестиций (ROI):</strong> Демонстрирует эффективность вложенных средств. Рассчитывается как Чистая прибыль / Инвестиции * 100%.</li>
          <li><strong>Рентабельность активов (ROA):</strong> Показывает эффективность использования активов компании. Рассчитывается как Чистая прибыль / Активы * 100%.</li>
          <li><strong>Рентабельность собственного капитала (ROE):</strong> Отражает отдачу от вложенного собственниками капитала. Рассчитывается как Чистая прибыль / Собственный капитал * 100%.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-500" />
          Как анализировать рентабельность бизнеса
        </h3>
        <p>
          Анализ рентабельности позволяет выявить сильные и слабые стороны бизнеса, определить потенциальные области для улучшения и принять обоснованные управленческие решения. При анализе рентабельности рекомендуется:
        </p>
        <ul>
          <li><strong>Сравнивать показатели с отраслевыми стандартами.</strong> Это позволит оценить эффективность бизнеса относительно конкурентов.</li>
          <li><strong>Отслеживать динамику показателей во времени.</strong> Важна не только абсолютная величина показателей, но и их изменение в течение времени.</li>
          <li><strong>Анализировать рентабельность по сегментам бизнеса.</strong> Это поможет выявить наиболее и наименее прибыльные направления деятельности.</li>
          <li><strong>Учитывать сезонность и цикличность бизнеса.</strong> В некоторых отраслях показатели рентабельности могут значительно колебаться в зависимости от сезона.</li>
        </ul>

        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800 my-6">
          <h4 className="text-emerald-700 dark:text-emerald-300 font-medium">Особенности рентабельности в разных отраслях экономики России</h4>
          <p className="text-emerald-600 dark:text-emerald-400 text-sm mb-0">
            Средние показатели рентабельности существенно различаются в зависимости от отрасли. Так, в IT-секторе средняя чистая маржа может достигать 15-20%, в розничной торговле - 3-5%, в производстве - 8-12%, в сфере услуг - 10-15%. На средние показатели также влияют макроэкономические условия, налоговая политика и специфика внутриотраслевой конкуренции.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <CircleDollarSign className="w-5 h-5 text-emerald-500" />
          Факторы, влияющие на рентабельность
        </h3>
        <p>
          На рентабельность бизнеса влияет множество факторов, включая:
        </p>
        <ul>
          <li><strong>Ценовая политика.</strong> Правильно выстроенная ценовая стратегия может значительно повысить рентабельность.</li>
          <li><strong>Структура затрат.</strong> Оптимизация постоянных и переменных расходов напрямую влияет на все показатели рентабельности.</li>
          <li><strong>Масштаб бизнеса.</strong> Экономия на масштабе позволяет снизить удельные затраты и повысить рентабельность.</li>
          <li><strong>Операционная эффективность.</strong> Оптимизация бизнес-процессов позволяет снизить расходы и повысить рентабельность.</li>
          <li><strong>Использование активов.</strong> Эффективное управление активами повышает их отдачу и увеличивает рентабельность.</li>
          <li><strong>Структура капитала.</strong> Соотношение собственного и заемного капитала влияет на рентабельность собственного капитала.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <BarChart className="w-5 h-5 text-emerald-500" />
          Способы повышения рентабельности бизнеса
        </h3>
        <p>
          Существует несколько ключевых направлений для повышения рентабельности бизнеса:
        </p>
        <ol>
          <li><strong>Увеличение выручки:</strong> расширение ассортимента, привлечение новых клиентов, увеличение среднего чека, выход на новые рынки.</li>
          <li><strong>Снижение себестоимости:</strong> оптимизация закупок, автоматизация производства, внедрение ресурсосберегающих технологий.</li>
          <li><strong>Сокращение операционных расходов:</strong> оптимизация бизнес-процессов, автоматизация рутинных операций, аутсорсинг непрофильных функций.</li>
          <li><strong>Оптимизация налогообложения:</strong> выбор оптимального налогового режима, использование законных налоговых льгот и преференций.</li>
          <li><strong>Эффективное управление оборотным капиталом:</strong> сокращение периода оборота запасов и дебиторской задолженности, увеличение периода оборота кредиторской задолженности.</li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Типичные ошибки при анализе рентабельности
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Игнорирование отраслевой специфики при сравнении показателей</li>
            <li>Фокус только на краткосрочной рентабельности в ущерб долгосрочному развитию</li>
            <li>Анализ рентабельности без учета масштаба бизнеса и стадии его развития</li>
            <li>Использование устаревших или некорректных данных для расчетов</li>
            <li>Игнорирование влияния макроэкономических факторов на показатели рентабельности</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <LayoutList className="w-5 h-5 text-emerald-500" />
          Особенности анализа рентабельности для разных типов бизнеса
        </h3>
        <h4>Производственные предприятия</h4>
        <p>
          Для производственных предприятий особое значение имеет анализ рентабельности продаж по отдельным видам продукции, а также рентабельность производства. Важно учитывать структуру себестоимости, долю постоянных и переменных затрат, загрузку производственных мощностей.
        </p>
        <h4>Торговые компании</h4>
        <p>
          Для торговых компаний ключевое значение имеет валовая маржа и оборачиваемость товарных запасов. Важно анализировать рентабельность по разным товарным группам и торговым точкам, а также учитывать сезонность спроса.
        </p>
        <h4>Сервисные компании</h4>
        <p>
          Для сервисных компаний важно анализировать рентабельность в разрезе отдельных услуг и клиентов. Ключевое значение имеет эффективность использования человеческих ресурсов, поскольку затраты на персонал обычно составляют основную часть расходов.
        </p>
        <h4>IT-компании и стартапы</h4>
        <p>
          Для IT-компаний и стартапов традиционный анализ рентабельности может быть дополнен специфическими метриками, такими как CAC (стоимость привлечения клиента), LTV (пожизненная ценность клиента), Burn Rate (скорость расходования средств) и период окупаемости инвестиций в разработку новых продуктов.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор рентабельности поможет вам проанализировать эффективность вашего бизнеса, сравнить показатели со средними по отрасли и выявить области для оптимизации!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;